// WI-13: the world opens on a curve, and what a twist pays (REMEDIATION-PLAN §5 D2, decided 19 Oct).
// Pure and three-free: it reads acts (src/map/places/actCatalog.ts), the trip's stages and dates,
// and one child's events, and knows nothing about any one destination.
//
// Bands per island, by rarity (an act may name its own `unlock`):
//   open    about a third of the commons (at least 3): alive the first time the island is seen;
//   arrival the legendary(s) and one rare: on the check-in at that stage, or the stage's first day;
//   key     the rest: opened one at a time by act keys from twists, on the release curve.
// The curve (releaseTarget): before departure at most `share` (40 %) of the key band opens, at most
// one key a day; during the trip the rest opens evenly, at most two a day; after it, one a day.
// On each twist (drawPayout): behind the curve pays a key; level with it, a small chance of
// tomorrow's key; ahead, never. More twists means more collectibles, not faster acts.
// A world per child: everything here is folded from that child's own events.
import type { Rarity } from '../../shared/rewards'
import { RewardPaid } from '../../shared/rewards'
import { addDays, daysBetween, zoned } from '../../shared/time'
import { rng, type Rng } from './rng'

export type Band = 'open' | 'key' | 'arrival'

export interface CatalogAct {
  id: string
  label: string
  rarity: Rarity
  /** Overrides the band its rarity gives it. */
  unlock?: Band
}

/** How many commons open from the start: a third, at least 3 (all of them on a tiny island). */
export const openCount = (commons: number): number => Math.min(commons, Math.max(3, Math.ceil(commons / 3)))

/** Each act's band on one island (catalog order decides which commons open and which rare arrives). */
export function actBands(acts: readonly CatalogAct[]): Map<string, Band> {
  const out = new Map<string, Band>()
  const commons = acts.filter((a) => a.rarity === 'common' && !a.unlock)
  const open = new Set(commons.slice(0, openCount(commons.length)).map((a) => a.id))
  const arrivalRare = acts.find((a) => a.rarity === 'rare' && !a.unlock)?.id
  for (const a of acts) {
    if (a.unlock) out.set(a.id, a.unlock)
    else if (a.rarity === 'legendary' || a.id === arrivalRare) out.set(a.id, 'arrival')
    else out.set(a.id, open.has(a.id) ? 'open' : 'key')
  }
  return out
}

export interface ReleaseStage {
  id: string
  /** The island (place id) the stage stands on. */
  place: string
  start: string
  end: string
  /** The stage's own zone (B13, a multi-zone trip); else the trip's. Its dates are in this zone. */
  tz?: string
}

export interface ReleaseTrip {
  start: string
  end: string
  stages: readonly ReleaseStage[]
  /** The trip's timezone (arrival's date fallback is in trip time). */
  tz: string
}

export type Phase = 'before' | 'during' | 'after'

export function phaseOn(trip: Pick<ReleaseTrip, 'start' | 'end'>, day: string): Phase {
  return day < trip.start ? 'before' : day > trip.end ? 'after' : 'during'
}

/** The most keys a day the curve pays in each phase. */
export const KEYS_A_DAY: Record<Phase, number> = { before: 1, during: 2, after: 1 }

/** The stage on `day` (during), the first (before) or the last (after). */
export function stageOn(trip: ReleaseTrip, day: string): ReleaseStage | undefined {
  const p = phaseOn(trip, day)
  if (p === 'before') return trip.stages[0]
  if (p === 'after') return trip.stages.at(-1)
  return trip.stages.find((s) => s.start <= day && day <= s.end) ?? trip.stages[0]
}

/** The trip's islands, each once: the current (or first) stage's first, then on through the trip, then the earlier ones. */
export function islandOrder(trip: ReleaseTrip, day: string): string[] {
  const cur = stageOn(trip, day)
  const i = Math.max(0, cur ? trip.stages.indexOf(cur) : 0)
  const order = [...trip.stages.slice(i), ...trip.stages.slice(0, i)].map((s) => s.place)
  return [...new Set(order)]
}

/**
 * How many key-band acts should be open by the end of `day`, out of `k`. `runupStart` is the day the
 * child's run-up began (their first twist on this trip); `share` the part of the key band that opens
 * before departure (0.4).
 */
export function releaseTarget(k: number, trip: Pick<ReleaseTrip, 'start' | 'end'>, runupStart: string, day: string, share = 0.4): number {
  if (k <= 0) return 0
  const from = runupStart < trip.start ? runupStart : trip.start
  const pre = Math.min(k, Math.floor(share * k))
  const runup = Math.max(0, daysBetween(from, trip.start))
  const before = (d: string): number => {
    const e = daysBetween(from, d) + 1
    if (e <= 0 || !runup) return 0
    return Math.min(pre, Math.floor((pre * Math.min(e, runup)) / runup), e)
  }
  if (day < trip.start) return before(day)
  const reached = before(addDays(trip.start, -1))
  const tripDays = daysBetween(trip.start, trip.end) + 1
  const during = (d: string): number => {
    const e = Math.min(daysBetween(trip.start, d) + 1, tripDays)
    return Math.min(k, reached + Math.min(Math.ceil(((k - reached) * e) / tripDays), KEYS_A_DAY.during * e))
  }
  if (day <= trip.end) return during(day)
  return Math.min(k, during(trip.end) + daysBetween(trip.end, day))
}

// ---------------------------------------------------------------- one child's world

export interface WorldEvent {
  id: string
  type: string
  at: number
  personId: string
  payload: unknown
}

export interface ActRef {
  place: string
  act: string
}

export const actKey = (a: ActRef): string => `${a.place}:${a.act}`

export interface ChildWorld {
  /** Acts open for this child on the trip's islands (`place:act`). Islands off the trip aren't gated. */
  unlocked: Set<string>
  /** Locked act ids per trip island (the Map's director and taps skip these). */
  locked: Record<string, string[]>
  /** The key band, all trip islands. */
  keyBand: number
  /** Keys paid on the curve so far (bonus keys don't count), and today. */
  keysPaid: number
  keysToday: number
  /** Key-band acts still locked, in the order keys go to them today. */
  queue: ActRef[]
  /** The day the child's run-up began (their first twist on this trip). */
  runupStart: string
  phase: Phase
}

export interface WorldInput {
  trip: ReleaseTrip
  catalog: Readonly<Record<string, readonly CatalogAct[]>>
  events: readonly WorldEvent[]
  childId: string
  day: string
  now: number
}

/** The hour (trip time) on a stage's first day when its arrival acts open without a check-in. */
export const ARRIVAL_FALLBACK_HOUR = 17

/** Has the child arrived at this stage: a near check-in during it, or its first day's fallback hour. */
function arrived(stage: ReleaseStage, input: WorldInput, checkins: readonly { at: number }[]): boolean {
  const { now, trip } = input
  // B13: a stage's dates and its 17:00 fallback are in the stage's own zone (Seoul for a Korea stage).
  const tz = stage.tz ?? trip.tz
  const today = zoned(now, tz)
  if (today.date > stage.start || (today.date === stage.start && Number(today.time.slice(0, 2)) >= ARRIVAL_FALLBACK_HOUR)) return true
  return checkins.some((c) => {
    const d = zoned(c.at, tz).date
    return d >= stage.start && d <= stage.end
  })
}

export function paidTo(events: readonly WorldEvent[], childId: string): RewardPaid[] {
  const out: RewardPaid[] = []
  for (const e of [...events].sort((a, b) => a.at - b.at || (a.id < b.id ? -1 : 1))) {
    if (e.type !== 'reward.paid' || e.personId !== childId) continue
    const p = RewardPaid.safeParse(e.payload)
    if (p.success && p.data.for === childId) out.push(p.data)
  }
  return out
}

export function childWorld(input: WorldInput): ChildWorld {
  const { trip, catalog, events, childId, day } = input
  const places = [...new Set(trip.stages.map((s) => s.place))].filter((p) => catalog[p]?.length)
  const paid = paidTo(events, childId)
  const keys = paid.filter((p) => p.kind === 'key' && p.place && p.act)
  const keyed = new Set(keys.map((p) => actKey({ place: p.place!, act: p.act! })))
  const checkins = events.filter((e) => e.type === 'checkin' && e.personId === childId && (e.payload as { near?: boolean } | null)?.near)
  const unlocked = new Set<string>()
  const locked: Record<string, string[]> = {}
  const remaining = new Map<string, ActRef[]>()
  let keyBand = 0
  for (const place of places) {
    const acts = catalog[place]!
    const bands = actBands(acts)
    const here = trip.stages.filter((s) => s.place === place)
    const isHere = here.some((s) => arrived(s, input, checkins))
    locked[place] = []
    remaining.set(place, [])
    for (const a of acts) {
      const band = bands.get(a.id)!
      const k = actKey({ place, act: a.id })
      if (band === 'key') keyBand++
      const open = band === 'open' || (band === 'arrival' && isHere) || keyed.has(k)
      if (open) unlocked.add(k)
      else locked[place]!.push(a.id)
      if (band === 'key' && !keyed.has(k)) remaining.get(place)!.push({ place, act: a.id })
    }
    // Commons first, then rares, each in catalog order.
    const rank = (r: ActRef) => (acts.find((a) => a.id === r.act)?.rarity === 'common' ? 0 : 1)
    remaining.get(place)!.sort((a, b) => rank(a) - rank(b))
  }
  const queue = islandOrder(trip, day).flatMap((p) => remaining.get(p) ?? [])
  const curve = keys.filter((p) => !p.bonus)
  const firstDay = paid[0]?.day
  return {
    unlocked,
    locked,
    keyBand,
    keysPaid: curve.length,
    keysToday: curve.filter((p) => p.day === day).length,
    queue,
    runupStart: firstDay && firstDay < day ? firstDay : day,
    phase: phaseOn(trip, day),
  }
}

// ---------------------------------------------------------------- what a twist pays

export interface Collectible {
  /** `<place>:<name>`. */
  id: string
  name: string
  emoji: string
  /** Generated sticker art, when the kit has it (falls back to the emoji). */
  art?: string
}

export interface PrizeLike {
  id: string
  rarity: Rarity
}

export type Payout<P extends PrizeLike = PrizeLike> =
  | { kind: 'key'; place: string; act: string; label: string }
  | { kind: 'prize'; prize: P }
  | { kind: 'collectible'; item: Collectible; place: string; count: number; completes: boolean; bonusKey?: { place: string; act: string; label: string } }

export interface DrawInput<P extends PrizeLike> {
  /** Seeds the draw, so the same twist always pays the same. */
  twistId: string
  world: ChildWorld
  trip: ReleaseTrip
  day: string
  catalog: Readonly<Record<string, readonly CatalogAct[]>>
  /** The part of the key band that opens before the trip (0.4). */
  share: number
  /** Real prizes this child could win now, and the chance per twist (the parent's level). */
  prizes: readonly P[]
  prizeChance: number
  /** The collectible set per island, and how many of each this child has. */
  sets: (place: string) => readonly Collectible[]
  owned: ReadonlyMap<string, number>
}

/** Level with the curve: the chance a twist pays tomorrow's key today. */
export const EARLY_KEY_CHANCE = 0.15
/** A real prize's rarity once one is drawn (BUILD_PLAN_NEXT §4.2). */
export const PRIZE_RARITY: Record<Rarity, number> = { common: 70, rare: 25, legendary: 5 }

function hash(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619)
  return h >>> 0
}

const labelOf = (catalog: DrawInput<PrizeLike>['catalog'], r: ActRef) => catalog[r.place]?.find((a) => a.id === r.act)?.label ?? r.act

function pickPrize<P extends PrizeLike>(rand: Rng, prizes: readonly P[]): P | null {
  if (!prizes.length) return null
  let roll = rand() * 100
  let rarity: Rarity = 'common'
  for (const r of ['common', 'rare', 'legendary'] as const) {
    rarity = r
    if ((roll -= PRIZE_RARITY[r]) < 0) break
  }
  const tier = prizes.filter((p) => p.rarity === rarity)
  return tier.length ? rand.pick(tier) : null
}

/** Is a key due on this twist: behind the curve, or level with it and lucky (never over the day's cap). */
export function keyDue(world: ChildWorld, trip: ReleaseTrip, day: string, share: number, roll: number): boolean {
  if (!world.queue.length || world.keysToday >= KEYS_A_DAY[world.phase]) return false
  const target = releaseTarget(world.keyBand, trip, world.runupStart, day, share)
  if (world.keysPaid < target) return true
  if (world.keysPaid > target) return false
  const tomorrow = releaseTarget(world.keyBand, trip, world.runupStart, addDays(day, 1), share)
  return tomorrow > target && roll < EARLY_KEY_CHANCE
}

/** What one twist pays: exactly one of an act key, a real prize or a collectible (plus a set bonus key). */
export function drawPayout<P extends PrizeLike>(input: DrawInput<P>): Payout<P> {
  const rand = rng(hash(`twist|${input.twistId}`))
  const { world, trip, day, catalog } = input
  if (keyDue(world, trip, day, input.share, rand())) {
    const next = world.queue[0]!
    return { kind: 'key', ...next, label: labelOf(catalog, next) }
  }
  if (rand() < input.prizeChance) {
    const prize = pickPrize(rand, input.prizes)
    if (prize) return { kind: 'prize', prize }
  }
  const place = stageOn(trip, day)?.place ?? ''
  const set = input.sets(place)
  const item = rand.pick(set)
  const count = (input.owned.get(item.id) ?? 0) + 1
  const had = set.filter((c) => (input.owned.get(c.id) ?? 0) > 0).length
  const completes = count === 1 && had + 1 === set.length
  const bonus = completes ? world.queue[0] : undefined
  return { kind: 'collectible', item, place, count, completes, ...(bonus ? { bonusKey: { ...bonus, label: labelOf(catalog, bonus) } } : {}) }
}

/** How many of each collectible a child has (from their `reward.paid` events). */
export function ownedCollectibles(events: readonly WorldEvent[], childId: string): Map<string, number> {
  const out = new Map<string, number>()
  for (const p of paidTo(events, childId)) if (p.kind === 'collectible' && p.item) out.set(p.item, (out.get(p.item) ?? 0) + 1)
  return out
}

/** Three of the same fuse into gold. */
export const GOLD_AT = 3
